import PrismAsyncLight from 'react-syntax-highlighter/dist/esm/prism-async-light';

import { LANG_ALIASES } from './consts';

/**
 * Ленивая регистрация Prism языков для async-light билда. Каждый язык —
 * отдельный chunk, загружается только при появлении первого блока кода с
 * этим языком.
 *
 * Использование: `await ensureLanguage("typescript")` перед рендером.
 * `PrismAsyncLight` сам кеширует регистрации, повторные вызовы no-op.
 */
const LOADERS: Record<string, () => Promise<{ default: unknown }>> = {
	javascript: () =>
		import('react-syntax-highlighter/dist/esm/languages/prism/javascript'),
	typescript: () =>
		import('react-syntax-highlighter/dist/esm/languages/prism/typescript'),
	jsx: () => import('react-syntax-highlighter/dist/esm/languages/prism/jsx'),
	tsx: () => import('react-syntax-highlighter/dist/esm/languages/prism/tsx'),
	python: () =>
		import('react-syntax-highlighter/dist/esm/languages/prism/python'),
	java: () => import('react-syntax-highlighter/dist/esm/languages/prism/java'),
	kotlin: () =>
		import('react-syntax-highlighter/dist/esm/languages/prism/kotlin'),
	swift: () =>
		import('react-syntax-highlighter/dist/esm/languages/prism/swift'),
	go: () => import('react-syntax-highlighter/dist/esm/languages/prism/go'),
	rust: () => import('react-syntax-highlighter/dist/esm/languages/prism/rust'),
	cpp: () => import('react-syntax-highlighter/dist/esm/languages/prism/cpp'),
	csharp: () =>
		import('react-syntax-highlighter/dist/esm/languages/prism/csharp'),
	sql: () => import('react-syntax-highlighter/dist/esm/languages/prism/sql'),
	bash: () => import('react-syntax-highlighter/dist/esm/languages/prism/bash'),
	json: () => import('react-syntax-highlighter/dist/esm/languages/prism/json'),
	yaml: () => import('react-syntax-highlighter/dist/esm/languages/prism/yaml'),
	css: () => import('react-syntax-highlighter/dist/esm/languages/prism/css'),
	markup: () =>
		import('react-syntax-highlighter/dist/esm/languages/prism/markup'),
	markdown: () =>
		import('react-syntax-highlighter/dist/esm/languages/prism/markdown'),
};

const registered = new Set<string>();
const inflight = new Map<string, Promise<void>>();

function resolveLangId(langId: string): string {
	const lower = langId.toLowerCase();
	const aliased = LANG_ALIASES[lower] ?? lower;
	if (aliased === 'html' || aliased === 'xml') return 'markup';
	return aliased;
}

export async function ensureLanguage(langId: string): Promise<void> {
	const resolved = resolveLangId(langId);
	if (!LOADERS[resolved] || registered.has(resolved)) return;

	const existing = inflight.get(resolved);
	if (existing) return existing;

	const promise = LOADERS[resolved]().then((mod) => {
		PrismAsyncLight.registerLanguage(resolved, mod.default);
		registered.add(resolved);
		inflight.delete(resolved);
	});
	inflight.set(resolved, promise);
	return promise;
}

export function getResolvedLangId(langId: string | null): string | null {
	if (!langId) return null;
	const resolved = resolveLangId(langId);
	return LOADERS[resolved] ? resolved : null;
}
